"use client";

import "./index.css";
import "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/clerk-react";
import { AppProviders, DynamicClerkProvider } from "@/lib/providers";
import { Toaster } from "@/components/ui/toaster";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { LanguageWrapper } from "@/components/LanguageWrapper";
import { createLocalizedRoutes } from "@/components/LocalizedRoute";
import { PageTracker } from "@/lib/hooks/usePageTracking";
import { AuthAnalytics } from "@/components/AuthAnalytics";
import ScrollToTop from "@/components/ScrollToTop";
import {
  LandingPage,
  DashboardPage,
  AccountPage,
  VideoListPage,
  VideoDetailPage,
  SharedVideoPage,
} from "./pages";
import InstagramCallbackPage from "./pages/instagram/InstagramCallbackPage";
import TikTokCallbackPage from "./pages/tiktok/TikTokCallbackPage";
import YouTubeCallbackPage from "./pages/youtube/YouTubeCallbackPage";
import TermsOfServicePage from "./pages/legal/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import AboutUsPage from "./pages/legal/AboutUsPage";

/**
 * Inner App component that has access to router context
 */
function AppContent() {
  const location = useLocation();

  return (
    <DynamicClerkProvider pathname={location.pathname}>
      <AppProviders>
        <LanguageWrapper>
          <PageTracker />
          <AuthAnalytics />
          <ScrollToTop />
          <Routes>
            {/* Public routes with localization */}
            {...createLocalizedRoutes({ path: "/", element: <LandingPage /> })}

            {/* Auth routes - English (no prefix) */}
            <Route
              path="/sign-in/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignIn
                    routing="path"
                    path="/sign-in"
                    signUpUrl="/sign-up"
                    fallbackRedirectUrl="/dashboard"
                    forceRedirectUrl="/dashboard"
                    signUpForceRedirectUrl="/dashboard"
                  />
                </div>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignUp
                    routing="path"
                    path="/sign-up"
                    signInUrl="/sign-in"
                    fallbackRedirectUrl="/dashboard"
                    forceRedirectUrl="/dashboard"
                    signInForceRedirectUrl="/dashboard"
                  />
                </div>
              }
            />

            {/* Language-specific auth routes for Swedish */}
            <Route
              path="/se/sign-in/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignIn
                    routing="path"
                    path="/se/sign-in"
                    signUpUrl="/se/sign-up"
                    fallbackRedirectUrl="/se/dashboard"
                    forceRedirectUrl="/se/dashboard"
                    signUpForceRedirectUrl="/se/dashboard"
                  />
                </div>
              }
            />
            <Route
              path="/se/sign-up/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignUp
                    routing="path"
                    path="/se/sign-up"
                    signInUrl="/se/sign-in"
                    fallbackRedirectUrl="/se/dashboard"
                    forceRedirectUrl="/se/dashboard"
                    signInForceRedirectUrl="/se/dashboard"
                  />
                </div>
              }
            />

            {/* Language-specific auth routes for Spanish */}
            <Route
              path="/es/sign-in/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignIn
                    routing="path"
                    path="/es/sign-in"
                    signUpUrl="/es/sign-up"
                    fallbackRedirectUrl="/es/dashboard"
                    forceRedirectUrl="/es/dashboard"
                    signUpForceRedirectUrl="/es/dashboard"
                  />
                </div>
              }
            />
            <Route
              path="/es/sign-up/*"
              element={
                <div className="min-h-screen bg-lavender flex items-center justify-center">
                  <SignUp
                    routing="path"
                    path="/es/sign-up"
                    signInUrl="/es/sign-in"
                    fallbackRedirectUrl="/es/dashboard"
                    forceRedirectUrl="/es/dashboard"
                    signInForceRedirectUrl="/es/dashboard"
                  />
                </div>
              }
            />

            {/* Shared video route - no auth needed, should work for all languages */}
            {...createLocalizedRoutes({
              path: "/share/:shareId",
              element: <SharedVideoPage />
            })}

            {/* Protected routes with localization */}
            {...createLocalizedRoutes({
              path: "/dashboard",
              element: (
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/account",
              element: (
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/videos",
              element: (
                <ProtectedRoute>
                  <VideoListPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/videos/:id",
              element: (
                <ProtectedRoute>
                  <VideoDetailPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/instagram/callback",
              element: (
                <ProtectedRoute>
                  <InstagramCallbackPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/tiktok/callback",
              element: (
                <ProtectedRoute>
                  <TikTokCallbackPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/youtube/callback",
              element: (
                <ProtectedRoute>
                  <YouTubeCallbackPage />
                </ProtectedRoute>
              )
            })}
            {...createLocalizedRoutes({
              path: "/terms",
              element: <TermsOfServicePage />
            })}
            {...createLocalizedRoutes({
              path: "/privacy",
              element: <PrivacyPolicyPage />
            })}
            {...createLocalizedRoutes({
              path: "/about",
              element: <AboutUsPage />
            })}

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LanguageWrapper>
        <Toaster />
      </AppProviders>
    </DynamicClerkProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
